/**
 * Session state shared between the axios interceptor (no React) and AuthContext.
 *
 * "Session expired" = the server really rejected the refresh token (401/403).
 * A network error or a 5xx is NEVER an expiry: the cookies stay, the user is
 * not signed out, the request is simply retried later.
 */
const COOLDOWN_MS = 60 * 1000;

let expiredAt = 0;
const listeners = new Set();

/** True while a refresh was rejected less than a minute ago (no new refresh is tried). */
export const isSessionExpired = () => expiredAt > 0 && Date.now() - expiredAt < COOLDOWN_MS;

/** Called once when the refresh is rejected; listeners fire only on the transition. */
export const markSessionExpired = () => {
  const already = isSessionExpired();
  expiredAt = Date.now();
  if (!already) listeners.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
};

/** A new sign-in (or a successful check) re-arms the automatic refresh. */
export const resetSessionExpired = () => { expiredAt = 0; };

export const onSessionExpired = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/** The server answered and refused the session (not a network / server problem). */
export const isAuthRejection = (error) => {
  const status = error?.response?.status;
  return status === 401 || status === 403;
};

/** No answer (offline, DNS, timeout) or a server error: says nothing about the session. */
export const isServiceUnavailable = (error) => {
  const status = error?.response?.status;
  return !error?.response || status >= 500;
};

const PENDING_LOGOUT_KEY = 'ridalo:pendingLogout';
/** A logout that could not reach the server must be replayed (cookies still there). */
export const setPendingLogout = (on) => {
  try {
    if (on) localStorage.setItem(PENDING_LOGOUT_KEY, '1');
    else localStorage.removeItem(PENDING_LOGOUT_KEY);
  } catch { /* private mode */ }
};
export const hasPendingLogout = () => {
  try { return localStorage.getItem(PENDING_LOGOUT_KEY) === '1'; } catch { return false; }
};
